import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '../stores/auth';
const Home = () => import('../pages/Home.vue');
const Login = () => import('../pages/auth/Login.vue');
const Register = () => import('../pages/auth/Register.vue');
const ChangePassword = () => import('../pages/auth/ChangePassword.vue');
const Profile = () => import('../pages/auth/Edit.vue');
const ForgotPassword = () => import('../pages/auth/ForgotPassword.vue');
const ResetPassword = () => import('../pages/auth/ResetPassword.vue');
const List = () => import('../pages/diner/List.vue');
const Show = () => import('../pages/diner/Show.vue');
const Mesa = () => import('../pages/diner/Mesa.vue');
const OrdenMesa = () => import('../pages/diner/OrdenMesa.vue');
const About = () => import('../pages/diner/About.vue');
const Error404 = () => import('../pages/errors/404.vue');
const Error403 = () => import('../pages/errors/403.vue');
const Error500 = () => import('../pages/errors/500.vue');
const BASE_DESCRIPTION = 'Descubre restaurantes y visualiza sus platos en video antes de ordenar. Escanea el QR de tu mesa y explora el menú al instante, sin descargar ninguna app.';
const routes = [
    {
        path: '/',
        name: 'home',
        component: Home,
        meta: {
            title: 'Kuere — Menús digitales con video para restaurantes',
            description: BASE_DESCRIPTION,
        },
    },
    {
        path: '/login/:qr_name',
        name: 'Login',
        component: Login,
        meta: {
            title: 'Iniciar sesión · Kuere',
            description: 'Inicia sesión en Kuere para guardar tus platos favoritos y calificar tu experiencia.',
        },
    },
    {
        path: '/register/:qr_name',
        name: 'Register',
        component: Register,
        meta: {
            title: 'Crear cuenta · Kuere',
            description: 'Crea tu cuenta en Kuere y guarda tus platos favoritos, califica lo que pruebas y recibe recomendaciones personalizadas.',
        },
    },
    {
        path: '/password-change/:qr_name',
        name: 'ChangePassword',
        component: ChangePassword,
        meta: {
            title: 'Cambiar contraseña · Kuere',
            description: 'Actualiza la contraseña de tu cuenta Kuere.',
        },
    },
    {
        path: '/forgot-password/:qr_name',
        name: 'ForgotPassword',
        component: ForgotPassword,
        meta: {
            title: 'Recuperar contraseña · Kuere',
            description: 'Recupera el acceso a tu cuenta Kuere.',
        },
    },
    {
        path: '/reset-password/:qr_name/:token',
        name: 'password.reset',
        component: ResetPassword,
        meta: {
            title: 'Nueva contraseña · Kuere',
            description: 'Establece una nueva contraseña para tu cuenta Kuere.',
        },
    },
    {
        path: '/profile/:qr_name',
        name: 'profile',
        component: Profile,
        meta: {
            requiresAuth: true,
            title: 'Mi perfil · Kuere',
            description: 'Gestiona los datos de tu cuenta en Kuere.',
        },
    },
    {
        path: '/orden/:diner_token',
        name: 'OrdenMesa',
        component: OrdenMesa,
        meta: {
            title: 'Mi orden · Kuere',
            description: 'Sigue el estado de tu pedido en la mesa.',
        },
    },
    {
        path: '/mesa/:qr_name/:qr_token',
        name: 'Mesa',
        component: Mesa,
        meta: {
            title: 'Pedir en mesa · Kuere',
            description: 'Agrega platos a tu pedido de mesa escaneando el QR de tu asiento.',
        },
    },
    {
        path: '/diner/:qr_name',
        name: 'Show',
        component: Show,
        meta: {
            title: 'Menú · Kuere',
            description: 'Explora el menú del restaurante con videos e imágenes reales de cada plato.',
        },
    },
    {
        path: '/about/:qr_name',
        name: 'about',
        component: About,
        meta: {
            title: 'Acerca de Kuere',
            description: BASE_DESCRIPTION,
        },
    },
    {
        path: '/:qr_name',
        name: 'List',
        component: List,
        meta: {
            title: 'Menú · Kuere',
            description: 'Explora el menú del restaurante con videos e imágenes reales de cada plato.',
        },
    },
    {
        path: '/403',
        name: 'errors.403',
        component: Error403,
        meta: { title: 'Acceso denegado · Kuere' },
    },
    {
        path: '/500',
        name: 'errors.500',
        component: Error500,
        meta: { title: 'Error del servidor · Kuere' },
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'errors.404',
        component: Error404,
        meta: { title: 'Página no encontrada · Kuere' },
    },
];
const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
});
// ─── Helper: actualiza un <meta> por atributo ─────────────────────────────────
function setMeta(attr, value, content) {
    let el = document.querySelector(`meta[${attr}="${value}"]`);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, value);
        document.head.appendChild(el);
    }
    el.setAttribute('content', content);
}
// ─── Guard: SEO + auth ────────────────────────────────────────────────────────
router.beforeEach(async (to, _from, next) => {
    const auth = useAuthStore();
    // Auth guard
    if (to.meta.requiresAuth && !auth.isLogged) {
        return next({ name: 'Login', params: { qr_name: to.params.qr_name } });
    }
    if (to.name === 'Login' && auth.isLogged) {
        return next({ name: 'Show', params: { qr_name: to.params.qr_name } });
    }
    // Título de pestaña
    const title = to.meta.title ?? 'Kuere';
    document.title = title;
    // Descripción
    const description = to.meta.description ?? BASE_DESCRIPTION;
    setMeta('name', 'description', description);
    // Open Graph
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', `https://app.kuere.co${to.path}`);
    // Twitter Card
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    next();
});
export default router;
//# sourceMappingURL=index.js.map